import { useCallback, useEffect, useRef, useState } from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";

import { getStationStateHubUrl } from "../services/StationStateApi";
import { Alert, AlertsPayload, normalizeAlerts } from "../services/alerts.types";

export type LiveConnectionStatus = "connecting" | "connected" | "reconnecting" | "disconnected";

export interface UseAlertsResult {
  /** Alertas ativos (último push do hub, ou o snapshot recebido ao conectar). */
  alerts: Alert[];
  /** Já recebeu ao menos um payload do hub (distingue "sem alertas" de "ainda carregando"). */
  hasReceived: boolean;
  /** Epoch (ms) de quando o payload foi recebido — base para interpolar o elapsed. */
  receivedAt: number;
  /** Epoch (ms) que avança a cada segundo (congela quando pausado) — dispara o re-render dos timers. */
  now: number;
  /** Estado da conexão em tempo real. */
  status: LiveConnectionStatus;
  /** Mensagem amigável quando a via realtime falha (os últimos alertas seguem visíveis). */
  error: string | null;
}

/**
 * Assina os alertas em tempo real do dashboard (dashboard-alertas).
 *
 * - Conecta no hub SignalR `/hubs/station-state` (o mesmo do semáforo); no `OnConnectedAsync`
 *   o servidor já envia os alertas vigentes (Clients.Caller) — é o paint inicial, sem REST.
 * - Empurra `AlertsChanged` quando o conjunto de alertas muda (aparece/some/cruza limiar).
 * - Conta `elapsedSeconds` localmente entre pushes (tick de 1s) a partir do `generatedAt`.
 * - `paused`: congela a aplicação de pushes e o tick.
 */
export const useAlerts = (paused: boolean): UseAlertsResult => {
  const [payload, setPayload] = useState<AlertsPayload | null>(null);
  const [hasReceived, setHasReceived] = useState<boolean>(false);
  const [receivedAt, setReceivedAt] = useState<number>(() => Date.now());
  const [now, setNow] = useState<number>(() => Date.now());
  const [status, setStatus] = useState<LiveConnectionStatus>("connecting");
  const [error, setError] = useState<string | null>(null);

  const pausedRef = useRef(paused);
  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  const applyAlerts = useCallback((raw: any) => {
    if (pausedRef.current) return; // congela enquanto pausado
    const normalized = normalizeAlerts(raw);
    if (!normalized) return;
    setPayload(normalized);
    setHasReceived(true);
    const ts = Date.now();
    setReceivedAt(ts);
    setNow(ts);
  }, []);

  useEffect(() => {
    let disposed = false;
    let connection: HubConnection;

    try {
      connection = new HubConnectionBuilder()
        .withUrl(getStationStateHubUrl())
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();
    } catch {
      setStatus("disconnected");
      setError("Não foi possível iniciar a conexão em tempo real.");
      return;
    }

    connection.on("AlertsChanged", (p: any) => applyAlerts(p));
    connection.onreconnecting(() => setStatus("reconnecting"));
    connection.onreconnected(() => {
      setStatus("connected");
      setError(null);
    });
    connection.onclose(() => {
      if (!disposed) setStatus("disconnected");
    });

    setStatus("connecting");
    connection
      .start()
      .then(() => {
        if (!disposed) {
          setStatus("connected");
          setError(null);
        }
      })
      .catch(() => {
        if (!disposed) {
          setStatus("disconnected");
          setError("Sem conexão em tempo real — os alertas podem estar desatualizados.");
        }
      });

    return () => {
      disposed = true;
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop().catch(() => {});
      }
    };
  }, [applyAlerts]);

  // Tick local de 1s para os timers dos alertas avançarem entre pushes.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!pausedRef.current) setNow(Date.now());
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  return {
    alerts: payload?.alerts ?? [],
    hasReceived,
    receivedAt,
    now,
    status,
    error,
  };
};

export default useAlerts;
